import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormControlProps,
  FormGroup,
  FormHelperText,
  FormLabel,
  useTheme,
} from "@mui/material";
import React, { forwardRef, useImperativeHandle } from "react";
import {
  FieldPath,
  FieldPathValue,
  FieldValues,
  useController,
  useFormContext,
} from "react-hook-form";
import { visuallyHidden } from "@mui/utils";
import { useFormsLocale } from "./FormsLocale";

type WithLabel = { label: React.ReactNode; "aria-labelledby"?: never };
type WithExternalLabel = { label?: never; "aria-labelledby": string };

type CheckboxGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = string | number,
> = Omit<FormControlProps, "ref" | "label" | "aria-labelledby" | "aria-label"> &
  (WithLabel | WithExternalLabel) & {
    id: string;
    name: TName;
    disabled?: boolean;
    required?: boolean;
    selectAllLabel?: React.ReactNode;
    selectAll?: boolean;
    requiredErrorMessage?: string; // TODO accept a function for translation
    helperText?: React.ReactNode;
    options: { label: React.ReactNode; value: TValue; disabled?: boolean }[];
    singularLabel?: string; // for error message translation
  };

export type FocusableInputHandle = { focus: () => void };

function CheckboxGroupImpl<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = string | number,
>(
  {
    label,
    name,
    id,
    options,
    disabled = false,
    required = false,
    selectAll = false,
    selectAllLabel,
    singularLabel,
    requiredErrorMessage,
    helperText = required ? " " : "", // leave space for error message to prevent layout shift
    ...formControlProps
  }: CheckboxGroupProps<TFieldValues, TName, TValue>,
  ref: React.Ref<FocusableInputHandle>,
) {
  const {
    checkboxGroup: {
      required: requiredText,
      selectAll: selectAllText,
      selectedDescription,
    },
  } = useFormsLocale();
  const { control } = useFormContext<TFieldValues>();
  // TODO accept rules and merge with required rules

  const rules = {
    validate: (value: any) => {
      if (required && (!value || value.length === 0)) {
        return requiredErrorMessage || requiredText(label, singularLabel);
      }
    },
  };
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues, TName>({
    name,
    control,
    rules,
    defaultValue: [] as FieldPathValue<TFieldValues, TName>,
  });

  const helperTextId = `${id}-helper-text`;
  const hasAriaLabelledBy = Boolean(formControlProps["aria-labelledby"]);

  //   TODO support subheading

  const checkedCount = field.value?.length || 0;
  const totalCount = options.length;
  const allChecked = checkedCount === totalCount && totalCount > 0;
  const someChecked = checkedCount > 0 && !allChecked;
  const theme = useTheme();

  const firstCheckboxRef = React.useRef<HTMLInputElement>(null);
  const setFirstCheckboxRef = (node: HTMLInputElement | null) => {
    field.ref(node); // for RHF registration
    firstCheckboxRef.current = node; // for external focus
  };
  useImperativeHandle(
    ref,
    () => ({
      focus: () => firstCheckboxRef.current?.focus(),
    }),
    [],
  );

  return (
    <FormControl
      component="fieldset"
      variant="standard"
      id={id}
      aria-describedby={helperTextId}
      error={!!error}
      required={required}
      {...formControlProps}
    >
      {!hasAriaLabelledBy && (
        <FormLabel component="legend">
          {label} <Box sx={visuallyHidden}>, {options.length} options</Box>
        </FormLabel>
      )}
      <FormHelperText id={helperTextId}>
        {error ? error.message : helperText}
      </FormHelperText>
      <FormGroup
        aria-invalid={!!error}
        onBlurCapture={(event) => {
          const next = event.relatedTarget as Node | null;
          if (!next || !event.currentTarget.contains(next)) {
            // focus has left the group, trigger onBlur for react-hook-form validation
            field.onBlur();
          }
        }}
      >
        {selectAll && (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(_, checked) => {
                  if (checked) {
                    field.onChange(options.map((option) => option.value));
                  } else {
                    field.onChange([]);
                  }
                }}
                sx={{
                  color: error ? theme.palette.error.main : undefined,
                }}
                checked={allChecked}
                indeterminate={someChecked}
                slotProps={{
                  input: {
                    "aria-describedby": helperTextId,
                    "aria-invalid": !!error,
                  },
                }}
                disabled={disabled}
              />
            }
            label={
              <>
                {selectAllLabel || selectAllText}
                <Box sx={visuallyHidden}>
                  , {selectedDescription(checkedCount, totalCount)}
                </Box>
              </>
            }
          />
        )}
        {selectAll && (
          <Box aria-hidden sx={{ my: 1, mx: -2 }}>
            <Divider />
          </Box>
        )}
        {options.map((option, index) => (
          <FormControlLabel
            key={`${option.value}`}
            disabled={disabled || option.disabled}
            control={
              <Checkbox
                //   color prop doesn't work for unselected error state, so we have to set the color in sx
                sx={{
                  color: error ? theme.palette.error.main : undefined,
                }}
                slotProps={{
                  input: {
                    id: `${id}-${option.value}`,
                    ref: index === 0 ? setFirstCheckboxRef : undefined, // focus first checkbox for the group on error
                    "aria-invalid": !!error, // for screen readers, indicate that the checkbox group is in an error state
                    "aria-describedby": helperTextId, // for screen readers to read aloud helper text
                  },
                }}
                disabled={disabled || option.disabled}
                value={option.value}
                checked={field.value?.includes(option.value)}
                onChange={(_, nextChecked) => {
                  field.onChange(
                    nextChecked
                      ? [...field.value, option.value]
                      : field.value.filter((v: TValue) => v !== option.value),
                  );
                }}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
}

export const CheckboxGroup = forwardRef(CheckboxGroupImpl) as <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TValue = string | number,
>(
  props: CheckboxGroupProps<TFieldValues, TName, TValue> &
    React.RefAttributes<FocusableInputHandle>,
) => React.ReactElement;
